<template>
  <div>
    <template v-if="level === 'portfolio'">
      <div :class="$style['portfolio-section-title']">{{ sectionTitle }}</div>
      <mds-fieldset size="small">
        <div :class="$style['fee-choice__wrapper']">
          <mds-button
            v-if="showClickToAddButton"
            :class="$style['plus-icon']"
            variation="flat"
            icon="plus"
            type="button"
            size="small"
            @click="handleFeeChoiceAction"
          >
            {{ translate('clickToAdd') }}
          </mds-button>
          <mds-section
            v-else
            :class="$style['fee-schedule-text-box']"
            :size="7"
            border="none"
            container
          >
            <template #mds-section-title>
              <span> {{ portfolioLevelFeeName }} </span>
            </template>
            <span v-if="isMaxFee" :class="$style['fee-schedule-text-box-detail']">
              {{ portfolioLevelMaxFeeDescription }}
            </span>
            <template
              v-if="!settings.isActualWorkflow || !isVariousFeeScheduleId"
              #mds-section-actions
            >
              <mds-button
                :class="$style['fee-schedule-text-box-detail-button']"
                size="small"
                @click="handleFeeChoiceAction"
              >
                {{ feeChoiceActionButtonText }}
              </mds-button>
            </template>
          </mds-section>
        </div>
      </mds-fieldset>
    </template>
    <template v-else-if="level === 'holding'">
      <div :class="$style['fee-choice__wrapper']">
        <mds-button
          v-if="showClickToAddButton"
          :class="$style['plus-icon']"
          variation="flat"
          icon="plus"
          type="button"
          size="small"
          @click="handleFeeChoiceAction"
        >
          {{ translate('clickToAdd') }}
        </mds-button>
        <div v-else :class="$style['fee-choice']" @click="handleFeeChoiceAction">
          <div :class="$style['fee-choice-text']">
            {{ holdingLevelFeeName }}
          </div>
          <div :class="$style['fee-choice-change']">
            {{ viewFeeActionButtonText }}
          </div>
        </div>
      </div>
    </template>
    <template v-if="showFeeScheduleModal">
      <fee-schedule-summary-list-modal
        v-model="showFeeScheduleModal"
        :mwc-id="getMwcIdForChild('fee-schedule-modal')"
        :currency-symbol="currencySymbol"
        :fee-schedule-id="feeScheduleId"
        :environment="environment"
        :user-info="userInfo"
        :asset-based-fee-config="assetBasedFeeConfig"
        :fee-schedules="allFeeScheduleList"
        @fee-schedules-updated="$emit('fee-schedules-updated', $event)"
        @back-button-clicked="closeFeeScheduleModal"
        @active-schedule-changed="updateSelectedFeeSchedule"
      />
    </template>
  </div>
</template>

<script>
import MwcVueHelperMixin, { MwcConfigMixin } from 'mwc-vue-component-helper';
import { MdsButton } from '@mds/button';
import MdsFieldset from '@mds/fieldset';
import MdsSection from '@mds/section';
import FeeScheduleSummaryListModal from './FeeScheduleSummaryListModal';
import { getFeeChoiceLabelKey, isMaxFee, isHistoricMaxFee } from '../../../utils/utils';

export default {
  name: 'fee-choice',
  components: {
    FeeScheduleSummaryListModal,
    MdsButton,
    MdsFieldset,
    MdsSection,
  },
  mixins: [MwcVueHelperMixin, MwcConfigMixin],
  props: {
    mwcId: {
      type: String,
      required: true,
    },
    currencySymbol: {
      type: String,
      default: '$',
    },
    assetBasedFeeConfig: {
      type: Object,
      default: () => {},
    },
    environment: String,
    userInfo: Object,
    level: {
      type: String,
      default: 'portfolio',
    },
    feeScheduleId: {
      type: String,
      default: '',
    },
    advisoryFeeSchedule: {
      type: Object,
    },
    isVariousFeeScheduleId: {
      type: Boolean,
      default: false,
    },
    allFeeScheduleList: {
      type: Array,
      default: () => [],
    },
    securityId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      defaultConfig: {
        settings: {
          languageId: this.getComponentConfig().settings.languageId || 'en-US',
        },
      },
      showFeeScheduleModal: false,
      selectedFeeSchedule: null,
    };
  },
  computed: {
    defaultVals() {
      return this.settings?.preference;
    },
    showClickToAddButton() {
      if (this.level === 'portfolio') {
        return !this.feeScheduleId && !this.isVariousFeeScheduleId;
      }
      return !this.selectedFeeSchedule;
    },
    sectionTitle() {
      return this.translate(getFeeChoiceLabelKey(this.assetBasedFeeConfig.feeScheduleLabelType));
    },
    isMaxFee() {
      return this.selectedFeeSchedule?.isMaxFee;
    },
    variousFeeScheduleText() {
      const vahLabelKey = this.settings.isActualWorkflow
        ? 'variesAcrossInvestments'
        : 'variesAcrossHoldings';
      return this.translate(vahLabelKey);
    },
    maxFeeAmountTxt() {
      const maxFee = this.formatter.number(this.selectedFeeSchedule.maxFee, 'shortDecimal');
      return `${maxFee}%`;
    },
    portfolioLevelMaxFeeDescription() {
      return `${this.translate('maxFee')} ${this.maxFeeAmountTxt}`;
    },
    portfolioLevelFeeName() {
      if (this.isVariousFeeScheduleId) {
        return this.variousFeeScheduleText;
      }
      const { name, programName } = this.selectedFeeSchedule ?? {};
      const isMaxFeeInActualWorkflow =
        isMaxFee(this.selectedFeeSchedule) && this.settings.isActualWorkflow;
      if (isMaxFeeInActualWorkflow || isHistoricMaxFee(this.selectedFeeSchedule)) {
        return programName;
      }
      return name;
    },
    holdingLevelFeeName() {
      const { name, programName } = this.selectedFeeSchedule ?? {};
      if (isMaxFee(this.selectedFeeSchedule)) {
        if (this.settings.isActualWorkflow) {
          return `${this.maxFeeAmountTxt} ${programName}`;
        }
        return `${this.maxFeeAmountTxt} ${name}`;
      }
      if (isHistoricMaxFee(this.selectedFeeSchedule)) {
        return programName;
      }
      return name;
    },
    viewFeeActionButtonText() {
      const labelKey = this.settings.isActualWorkflow ? 'view' : 'change';
      return this.translate(labelKey);
    },
    feeChoiceActionButtonText() {
      return this.isVariousFeeScheduleId ? this.translate('clear') : this.viewFeeActionButtonText;
    },
  },
  watch: {
    allFeeScheduleList() {
      this.getSelectedFeeScheduleInfo();
    },
    feeScheduleId() {
      this.getSelectedFeeScheduleInfo();
    },
  },
  created() {
    this.getSelectedFeeScheduleInfo();
  },
  methods: {
    getSelectedFeeScheduleInfo() {
      if (
        !Array.isArray(this.allFeeScheduleList) ||
        this.allFeeScheduleList.length === 0 ||
        !this.feeScheduleId
      ) {
        this.selectedFeeSchedule = null;
        return;
      }
      this.selectedFeeSchedule = this.allFeeScheduleList.find(
        s => s.feeScheduleId === this.feeScheduleId
      );
    },
    handleFeeChoiceAction(e) {
      e.preventDefault();
      if (this.isVariousFeeScheduleId && !this.settings.isActualWorkflow) {
        // clear potfolio level data
        this.selectedFeeSchedule = null;
        this.$emit('update-fee-schedule-id', this.defaultVals.feeScheduleId);
        return;
      }
      this.showFeeScheduleModal = true;
    },
    closeFeeScheduleModal() {
      this.$emit('before-close', () => {
        this.showFeeScheduleModal = !this.showFeeScheduleModal;
      });
    },
    updateSelectedFeeSchedule(feeSchedule) {
      this.$emit('before-close', () => {
        this.selectedFeeSchedule = feeSchedule;
        this.showFeeScheduleModal = false;
        this.$emit(
          'update-fee-schedule-id',
          feeSchedule ? feeSchedule.id : this.defaultVals.feeScheduleId,
          this.securityId
        );
      });
    },
  },
};
</script>

<style lang="scss" module>
@import '~@mds/constants';
@import '@mds/icon/src/icon_mixins';
@import '~@mds/typography';
@import '~@mds/link';

.portfolio-section-title {
  font-weight: bold;
  color: #6e6e6e;
  font-size: 14px;
  margin-bottom: $mds-space-quarter-x;
}
.fee-choice__wrapper {
  margin-bottom: $mds-space-1-x;

  .fee-schedule-text-box {
    min-height: 75px;

    .mds-section__header-container {
      margin-bottom: -$mds-space-1-and-a-half-x;
    }

    &-detail {
      @include mds-body-text-s();

      color: $mds-text-color-secondary-on-dark;

      &-button {
        margin-top: $mds-space-3-x;
        float: right !important;
      }
    }
  }

  .plus-icon {
    color: $mds-text-color-interactive-primary-on-dark;
    .mds-icon {
      @include mds-icon-color($mds-text-color-interactive-primary-on-dark);
    }
  }
  .fee-choice {
    color: $mds-text-color-secondary;
    display: flex;
    justify-content: space-between;

    &:hover {
      cursor: pointer;
      color: $mds-text-color-interactive-primary-on-dark;
      .fee-choice-change {
        opacity: 1;
      }
    }
    .fee-choice-text {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .fee-choice-change {
      color: $mds-text-color-interactive-primary-on-dark;
      margin-left: $mds-space-three-quarter-x;
      opacity: 0;
      transition: opacity 0.6s ease-out;
    }
  }
}
</style>
